// Session renewal.
//
// Login used to hand back only a Keycloak access token, so every session
// ended hard when it expired (8h on the flex2 realm) however active the user
// was — inspectors were signed out mid-inspection. flex2-api now also returns
// the refresh token, and POST /auth/refresh redeems it (the grant needs the
// API's client secret, so the browser can't go to Keycloak itself).
//
// Lifecycle:
//   - a login that returns a refresh token calls storeRefreshToken();
//   - scheduleRefresh() renews shortly before EITHER token expires (Keycloak's
//     refresh token idles out on its own clock), and again when a hidden tab
//     comes back after the moment passed;
//   - the shared 401 handler (src/api) calls refreshSession() once before
//     signing anyone out, and retries the request that failed.
//
// Any OTHER new access token (impersonation, org switch, magic link) clears
// the stored refresh token — see tokenAction — so a stale refresh token can
// never "renew" an impersonated session back into the admin's own.
import axios from "axios";
import store from "redux-store/store";
import { tokenAction } from "redux-store/auth/reducers";
import { isSsoSession, refreshWithKeycloak } from "auth/oidc";

export const REFRESH_TOKEN_KEY = "refreshToken";
export const REFRESH_EXPIRES_AT_KEY = "refreshTokenExpiresAt";

// Renew this long before the earlier of the two expiries.
const LEAD_MS = 2 * 60 * 1000;
const MIN_DELAY_MS = 15 * 1000;
const RETRY_TRANSIENT_MS = 30 * 1000;

// A bare instance: none of the shared interceptors (which sign out on 401 and
// would recurse into this module), and no stale bearer header.
const refreshClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 20000,
});

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (_e) {
    return null;
  }
};
const write = (key, value) => {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch (_e) {
    /* storage disabled — the session just won't renew */
  }
};

// exp (ms since epoch) from a JWT, or null.
export const jwtExpiresAt = (token) => {
  try {
    const seg = String(token).split(".")[1];
    if (!seg) return null;
    const json = JSON.parse(atob(seg.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.exp === "number" ? json.exp * 1000 : null;
  } catch (_e) {
    return null;
  }
};

export const getRefreshToken = () => read(REFRESH_TOKEN_KEY);

export const clearRefreshToken = () => {
  write(REFRESH_TOKEN_KEY, null);
  write(REFRESH_EXPIRES_AT_KEY, null);
};

let timer = null;

export function storeRefreshToken(refreshToken, refreshExpiresIn) {
  if (!refreshToken) return;
  write(REFRESH_TOKEN_KEY, refreshToken);
  // 0 / missing = Keycloak gave no idle expiry (offline token) — fall back to
  // the access token's clock alone.
  const secs = Number(refreshExpiresIn);
  write(REFRESH_EXPIRES_AT_KEY, secs > 0 ? String(Date.now() + secs * 1000) : null);
  scheduleRefresh();
}

let inflight = null;

// Renew the session once. Resolves true (renewed), false (the refresh token
// was refused — the session is genuinely over) or null (couldn't reach the
// server; try again later). Concurrent callers share one request.
export function refreshSession() {
  if (inflight) return inflight;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(false);
  // A single sign-on session renews with Keycloak itself (f2-web is public);
  // Keycloak answers 400 invalid_grant once the session is over.
  const renew = isSsoSession()
    ? refreshWithKeycloak(refreshToken)
        .then((t) => ({ data: { data: { accessToken: t.access_token, refreshToken: t.refresh_token, refreshExpiresIn: t.refresh_expires_in } } }))
        .catch((e) => Promise.reject(e?.response?.status === 400 ? { response: { status: 401 } } : e))
    : refreshClient.post("/auth/refresh", { refreshToken });
  inflight = renew
    .then((res) => {
      const data = res?.data?.data || {};
      if (!data.accessToken) return false;
      // Another tab (or a fresh login) may have replaced the session while
      // this was in flight — don't overwrite it with ours.
      if (getRefreshToken() !== refreshToken) return true;
      store.dispatch(tokenAction(data.accessToken)); // clears the old refresh token…
      storeRefreshToken(data.refreshToken || refreshToken, data.refreshExpiresIn); // …so store the new one after
      return true;
    })
    .catch((e) => (e?.response?.status === 401 ? false : null))
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

const nextRefreshAt = () => {
  if (!getRefreshToken()) return null;
  const accessExp = jwtExpiresAt(read("authToken"));
  const refreshExp = Number(read(REFRESH_EXPIRES_AT_KEY)) || null;
  const candidates = [accessExp, refreshExp].filter(Boolean);
  if (candidates.length === 0) return null;
  return Math.min(...candidates) - LEAD_MS;
};

export function scheduleRefresh() {
  if (timer) clearTimeout(timer);
  timer = null;
  const at = nextRefreshAt();
  if (at == null) return;
  timer = setTimeout(async () => {
    timer = null;
    const ok = await refreshSession();
    if (ok === null) {
      // Server unreachable: keep trying; the tokens may still be valid.
      timer = setTimeout(() => scheduleRefresh(), RETRY_TRANSIENT_MS);
    } else if (ok) {
      scheduleRefresh();
    }
    // false: nothing to do — the next request's 401 signs the user out.
  }, Math.max(MIN_DELAY_MS, at - Date.now()));
}

let started = false;

// Wire the timers once at app start.
export function startSessionRenewal() {
  if (started || typeof window === "undefined") return;
  started = true;
  scheduleRefresh();
  // Timers are throttled or frozen in background tabs and on sleeping
  // tablets; check again the moment the page is looked at.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    const at = nextRefreshAt();
    if (at != null && at <= Date.now()) {
      refreshSession().then(() => scheduleRefresh());
    } else {
      scheduleRefresh();
    }
  });
  // Another tab renewed or signed out: follow its tokens.
  window.addEventListener("storage", (e) => {
    if (e.key === "authToken" || e.key === REFRESH_TOKEN_KEY) scheduleRefresh();
  });
}
